// src/components/SavedRaidsPanelLite.jsx
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { JOB_CONFIG } from '../constants/battle/jobs';
import { useAuth } from '../contexts/AuthContext';
import { saveRaidCloudFromParty } from '../firebaseClient';
import { getFirestore, collection, getDocs } from 'firebase/firestore';

/* =========================================================
 * 유틸: OCR timestamp 문자열 포맷 (예: "2025.08.17 23:28:15")
 * ======================================================= */
function formatYMDHMS(tsStr) {
  const z2 = (v) => String(v).padStart(2, '0');
  const parts = String(tsStr || '')
    .replace(/[^\d]/g, ' ')
    .trim()
    .split(/\s+/)
    .map((n) => parseInt(n, 10));
  if (parts.length >= 6) {
    const [y, mo, da, hh, mi, ss] = parts;
    return `${y}-${z2(mo)}-${z2(da)} ${z2(hh)}:${z2(mi)}:${z2(ss)}`;
  }
  if (parts.length >= 5) {
    const [y, mo, da, hh, mi] = parts;
    return `${y}-${z2(mo)}-${z2(da)} ${z2(hh)}:${z2(mi)}:00`;
  }
  if (parts.length >= 3) {
    const [y, mo, da] = parts;
    return `${y}-${z2(mo)}-${z2(da)}`;
  }
  return '';
}

function roleKor(job, engraving) {
  const meta = (JOB_CONFIG[job]?.engravings || []).find((e) => e?.name === engraving);
  const role = meta?.role || null;
  if (!role) return '—';
  return role === 'supporter' ? '서포터' : '딜러';
}

// 숫자 전투력 표기: 소수 둘째 자리까지, .00은 제거
function formatPower(p) {
  const n = typeof p === 'string' ? parseFloat(String(p).replace(/,/g, '')) : p;
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2).replace(/\.00$/, '');
}

// 긴 메모는 잘라서 표시
function shorten(s, max = 40) {
  const t = String(s || '').trim();
  return t.length > max ? t.slice(0, max) + '…' : t;
}

/* =========================================================
 * 직업/각인 정규화
 * ======================================================= */
function normalizeJob(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.name || value.key || value.id || '';
}
function normalizeEngraving(value, job) {
  if (!value) return '';
  const s = typeof value === 'string' ? value : (value.name || value.key || value.id || '');
  const list = JOB_CONFIG[job]?.engravings || [];
  const hit = list.find(
    (e) =>
      e?.name === s ||
      e?.key === s ||
      e?.label === s
  );
  return hit?.name || s; // 저장 시 BA 포맷(name)으로 통일
}

/* =========================================================
 * 키 계산(BA와 동일 기준)
 * ======================================================= */
function makeCompositeKey(r) {
  const raidName   = r?.raidName   ?? r?.payload?.raidName   ?? '';
  const timestamp  = r?.timestamp  ?? r?.payload?.timestamp  ?? '';
  const battleTime = r?.battleTime ?? r?.payload?.battleTime ?? '';
  const base = `${raidName}__${timestamp}__${battleTime}`.trim();
  if (base.replace(/_/g, '').length > 0) return base;
  const cands = [
    r?.id, r?.docId, r?.key,
    `${r?.uid || ''}-${r?.timestamp || ''}`,
    r?.payload?.id, r?.payload?.docId, r?.payload?.key,
    `${r?.payload?.uid || ''}-${r?.payload?.timestamp || ''}`,
  ].filter(Boolean);
  return cands[0] || Math.random().toString(36).slice(2);
}

function primaryId(r) {
  return (
    r?.id || r?.docId || r?.key || `${r?.uid || ''}-${r?.timestamp || ''}` ||
    r?.payload?.id || r?.payload?.docId || r?.payload?.key ||
    `${r?.payload?.uid || ''}-${r?.payload?.timestamp || ''}` || ''
  );
}

/* =========================================================
 * 서버(개인 클라우드) 존재 여부 확인
 *  - compositeKey(raidName/timestamp/battleTime) 단위로 존재 체크
 * ======================================================= */
async function fetchUserCloudKeys(uid) {
  if (!uid) return new Set();
  const db = getFirestore();
  const colRef = collection(db, 'users', uid, 'raids');
  const snap = await getDocs(colRef);
  const toKey = (r) =>
    `${r.raidName || ''}__${r.timestamp || ''}__${r.battleTime || ''}`.trim();
  const set = new Set();
  snap.forEach((d) => set.add(toKey(d.data() || {})));
  return set;
}

/* =========================================================
 * 컴포넌트
 * ======================================================= */
export default function SavedRaidsPanelLite({
  records,                 // visibleAnalyses (gate 필터된 공격대 기록)
  selectedIds,             // 선택된 id 배열 (라디오처럼 1개만 쓰도록 상위에서 제어)
  onSelect,                // (nextIds: string[]) => void
  onRefresh,               // () => Promise<void> | void
  onLoadSelected,          // () => void
  onClearAll,              // () => Promise<void> | void
  isLeader,                // 공대장 여부
  onCalcRaidAverage,       // () => void
  onShowRaidAverage,       // () => void
}) {
  const [query, setQuery] = useState('');
  const { user } = useAuth();
  const meUid = user?.uid;

  // 서버(개인 클라우드) 존재 표시
  const [serverSavedKeys, setServerSavedKeys] = useState(new Set());

  // BA의 중복 기준과 동일한 compositeKey
  const keyOf = useCallback((r) => makeCompositeKey(r), []);

  // 내 기록 여부
  const isMine = useCallback((r) => {
    if (!meUid || !r) return false;
    if (r.authorUid && r.authorUid === meUid) return true;
    if (r?.payload?.authorUid && r.payload.authorUid === meUid) return true;
    if (r.ownerUid === meUid || r.uid === meUid || r.userUid === meUid) return true;
    if (r?.payload?.ownerUid === meUid || r?.payload?.uid === meUid || r?.payload?.userUid === meUid) return true;
    return false;
  }, [meUid]);

  // 선택된 실제 레코드
  const findBySelectedId = useCallback((id) => {
    const list = records || [];
    return list.find((r) => primaryId(r) === id);
  }, [records]);

  // 서버 존재 여부 갱신 (records/meUid 바뀔 때마다)
  useEffect(() => {
    fetchUserCloudKeys(meUid).then(setServerSavedKeys);
  }, [meUid, records]);

  // BA에서 클라우드 삭제 이벤트 처리
  useEffect(() => {
    const onDel = () => {
      fetchUserCloudKeys(meUid).then(setServerSavedKeys);
    };
    window.addEventListener('ba:cloudDeleted', onDel);
    return () => window.removeEventListener('ba:cloudDeleted', onDel);
  }, [meUid, records]);

  // 레코드 -> 렌더 항목
  const items = useMemo(() => {
    return (records || []).map((r) => {
      const id = primaryId(r);
      const job = normalizeJob(r.job || r?.payload?.job || '');
      const engraving = normalizeEngraving(r.engraving || r?.payload?.engraving || '', job);
      const nickname = r.nickname || r?.payload?.nickname || '';

      const timestampStr = r?.payload?.timestamp || r.timestamp;
      const ymdhms = formatYMDHMS(timestampStr);
      const role = roleKor(job, engraving);

      // 전투력/메모
      const rawPower = r.power ?? r?.payload?.power;
      const powerStr = formatPower(rawPower);
      const memoStr = shorten(r?.payload?.memo || '', 40);

      // 라벨
      const labelBase = `${nickname || '닉네임?'} / ${job || '직업?'}${engraving ? `(${engraving})` : ''} / ${role}`;
      const stamp = ymdhms ? `  · ${ymdhms}` : '';
      const powerPart = powerStr ? `  · 전투력 ${powerStr}` : '';
      const memoPart = memoStr ? `  · 메모 ${memoStr}` : '';
      const label = `${labelBase}${stamp}${powerPart}${memoPart}`;

      const compositeKey = keyOf(r);
      const mine = isMine(r);
      const cloudSavedServer = serverSavedKeys.has(compositeKey);

      return {
        id,
        label,
        raw: r,
        mine,
        compositeKey,
        cloudSavedServer,
      };
    });
  }, [records, keyOf, isMine, serverSavedKeys]);

  // 검색
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.label.toLowerCase().includes(q));
  }, [items, query]);

  // 선택 헬퍼
  const emitSelect = (next) => onSelect?.(next);
  const clearAllSelection = () => emitSelect([]);

  // 개인 클라우드 저장
  const handleSave = useCallback(
    async (recRaw) => {
      if (!meUid) {
        alert('로그인이 필요합니다.');
        return;
      }
      if (!isMine(recRaw)) return;

      try {
        const jobRaw =
          recRaw?.job ??
          recRaw?.payload?.job ?? '';

        const engravingRaw =
          recRaw?.jobEngraving ??
          recRaw?.payload?.jobEngraving ??
          recRaw?.engraving ??
          recRaw?.payload?.engraving ?? '';

        const job = normalizeJob(jobRaw);
        const jobEngraving = normalizeEngraving(engravingRaw, job);

        const payload = {
          raidName: recRaw?.raidName ?? recRaw?.payload?.raidName,
          timestamp: recRaw?.timestamp ?? recRaw?.payload?.timestamp,
          battleTime: recRaw?.battleTime ?? recRaw?.payload?.battleTime,
          mainTraits: recRaw?.mainTraits ?? recRaw?.payload?.mainTraits ?? [],
          additionalTraits: recRaw?.additionalTraits ?? recRaw?.payload?.additionalTraits ?? [],
          job,                           // ✅ 정규화된 직업
          jobEngraving,                  // ✅ 정규화된 직업각인(BA name 기준)
          level: recRaw?.level ?? recRaw?.payload?.level ?? '',
          power: recRaw?.power ?? recRaw?.payload?.power ?? '',
          memo: recRaw?.memo ?? recRaw?.payload?.memo ?? '',
          partyCode: recRaw?.partyCode ?? recRaw?.payload?.partyCode ?? null,
        };

        const { alreadyExists } = await saveRaidCloudFromParty(payload);

        if (alreadyExists) {
          alert('이미 클라우드에 저장된 항목입니다.');
        } else {
          alert('클라우드에 저장되었습니다.');
        }

        // 서버에서 실제로 들어갔는지 다시 읽어와 배지/버튼 동기화
        fetchUserCloudKeys(meUid).then(setServerSavedKeys);

        onRefresh?.();
      } catch (e) {
        console.error(e);
        alert(`저장 실패: ${e?.message || e}`);
      }
    },
    [isMine, meUid, onRefresh]
  );

  // 상단 툴바: 버튼 활성화 조건(개인 클라우드에 아직 없음)
  const selectedRecord = selectedIds.length === 1 ? findBySelectedId(selectedIds[0]) : null;
  const selectedIsMine = selectedRecord ? isMine(selectedRecord) : false;
  const selectedKey = selectedRecord ? keyOf(selectedRecord) : '';
  const canCloudSave = !!selectedRecord && selectedIsMine && !serverSavedKeys.has(selectedKey);

  return (
    <div className="basis-2/5 flex flex-col gap-2 flex-1 min-h-0">
      {/* 상단 버튼 */}
      <div className="flex flex-wrap gap-2">
        <button onClick={onRefresh} className="btn btn-sm btn-primary">
          새로고침
        </button>

        {isLeader && (
          <button
            onClick={onClearAll}
            disabled={(records || []).length === 0}
            className="btn btn-sm btn-danger"
            title="공격대 기록을 모두 비웁니다"
          >
            저장된 기록 비우기
          </button>
        )}

        {/* 선택 → 내 클라우드에 저장 */}
        <button
          type="button"
          onClick={() => selectedRecord && handleSave(selectedRecord)}
          className="btn btn-sm disabled:cursor-not-allowed"
          title="선택한 기록을 내 클라우드에 저장"
          disabled={!canCloudSave}
        >
          내 클라우드에 저장
        </button>
      </div>

      {/* 목록 카드 */}
      {(records || []).length > 0 ? (
        <div className="flex-1 basis-[0px] min-h-0 rounded-lg border border-zinc-950/10 bg-white overflow-hidden flex flex-col dark:border-white/10 dark:bg-zinc-900">
          {/* 검색 */}
          <div className="flex items-center gap-2 p-2 border-b border-zinc-950/10 dark:border-white/10">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="검색 (직업/닉네임/역할)"
              className="input-sm flex-1 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder:text-zinc-400 dark:border-white/10"
            />
            {selectedIds.length > 0 && (
              <button onClick={clearAllSelection} className="btn btn-sm">
                선택 해제
              </button>
            )}
          </div>

          {/* 리스트 */}
          <div className="flex-1 overflow-auto basis-[0px]">
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {filtered.map(({ id, label, mine, compositeKey, cloudSavedServer }) => {
                const selected = selectedIds.includes(id);
                const showSavedBadge = mine && cloudSavedServer;

                return (
                  <li key={id}>
                    <label
                      className={[
                        'flex items-start gap-2 p-2 cursor-pointer',
                        selected
                          ? 'bg-indigo-50 dark:bg-indigo-500/10'
                          : 'hover:bg-zinc-50 dark:hover:bg-white/5',
                      ].join(' ')}
                    >
                      {/* 단일 선택(라디오처럼) */}
                      <input
                        type="checkbox"
                        className="shrink-0 mt-0.5 size-4 rounded border-zinc-950/10 text-indigo-600 focus:ring-indigo-500 dark:border-white/10 dark:bg-zinc-800 dark:text-indigo-400 dark:focus:ring-indigo-400"
                        checked={selected}
                        onChange={() => onSelect?.([id])}
                        aria-checked={selected}
                      />

                      {/* 본문 */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-800 dark:text-zinc-200 whitespace-normal break-words leading-5">
                          {label}
                          {showSavedBadge && (
                            <span className="ml-2 inline-flex items-center rounded-full border px-1.5 py-0 text-[11px] bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-400/10 dark:text-sky-300 dark:border-sky-400/20">
                              클라우드에 저장됨
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="p-4 text-sm text-zinc-500 dark:text-zinc-400">검색 결과가 없습니다.</li>
              )}
            </ul>
          </div>

          {/* 하단 버튼 */}
          <div className="flex gap-2 p-2 border-t border-zinc-950/10 dark:border-white/10">
            <button
              onClick={onLoadSelected}
              disabled={selectedIds.length !== 1}
              className="btn btn-sm flex-1"
              title="기록을 1개 선택하면 상세를 표시합니다"
            >
              선택 기록 보기
            </button>
            {isLeader && (
              <button
                className="btn btn-sm flex-1"
                onClick={onCalcRaidAverage}
                title="딜러만 대상으로 평균(총피해, DPS) 계산"
              >
                공격대 평균 계산
              </button>
            )}
            <button
              onClick={onShowRaidAverage}
              className="btn btn-sm flex-1"
              title="지금까지 저장된 공격대 평균을 확인합니다"
            >
              공격대 평균 확인
            </button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-zinc-500 dark:text-zinc-400 flex-1 items-center justify-center basis-[0px] rounded-lg border border-zinc-950/10 bg-white overflow-hidden flex flex-col dark:border-white/10 dark:bg-zinc-900">
          저장된 공격대 기록이 없습니다.
        </div>
      )}
    </div>
  );
}
